import React, { useEffect, useState, useRef } from "react";
import ActionBar from "../../common/ActionBar";
import styled from "styled-components";
import _ from "lodash";
import { navigate } from "@reach/router";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { PrimaryCTAButton } from "../../common/Buttons";
import PartyService from "../../../services/PartyService";
import { getErrorMessage } from "../../../helpers/functions";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import labels from "../../../helpers/labels.json";
import { documentLabels } from "../../../helpers/constants";
import AgentListModal from "../../common/AgentListModal/AgentListDrawer";
import useAlert from "../../../hooks/useAlert";
import CustomSelect from "../../common/CustomSelect/CustomSelect";
import {
  makeStyles,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@material-ui/core";

const useStyles = makeStyles(() => ({
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
}));

function getAgentName(agents, isPrimary) {
  const agentList = isPrimary
    ? agents?.filter((el) => el.role === "primary")
    : agents?.filter((el) => el.role !== "primary");
  if (agentList?.length) {
    let final = [];
    for (const agent of agentList) {
      if (agent?.user?.name) {
        final.push(agent.user.name);
      }
    }
    return final.join(", ");
  }
}

const Index = ({ partyId, isFromListing = false }) => {
  const verifyBreadCrum = [
    _.startCase(labels.my_tasks),
    _.startCase(labels.verify),
    _.startCase(labels.party_details),
  ];

  const partyDetailBreadCrum = [
    _.startCase(labels.parties),
    _.startCase(labels.party_details),
  ];
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [party, setParty] = useState({});
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [primaryAgentList, setPrimaryAgentList] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const MTRef = useRef();
  const [opendrawer, setOpenDrawer] = useState(false);
  const { hideAlert, showAlert } = useAlert();
  const [partyCode, setPartyCode] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const classes = useStyles();

  const [value, setValue] = useState({
    partyCode: party?.partyCode || "",
    mailOption: "basic",
    whatsappService: false,
  });

  const inputs = [
    {
      name: "partyCode",
      label: "Party Code",
      menuItems: partyCode,
      type: "select",
    },
    {
      name: "customerName",
      label: "Customer Name",
      type: "text",
    },
    {
      name: "mailOption",
      label: "Mail Settings",
      menuItems: [
        { label: "Basic", value: "basic" },
        { label: "Premium", value: "premium" },
      ],
      type: "radio",
    },
    {
      name: "whatsappService",
      label: "Communication Access",
      menuItems: [{ label: "Whatsapp Service", value: false }],
      type: "radioService",
    },
  ];

  const onBreadcrumbClick = (selected) => {
    switch (_.snakeCase(selected)) {
      case labels.my_tasks:
        navigate("/dashboard");
        break;
      case labels.parties:
        navigate("/dashboard/case-manager/parties");
        break;
      case labels.verify:
        navigate("/dashboard/case-manager/verify-parties");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isFromListing) {
      setBreadcrumbs(partyDetailBreadCrum);
    } else {
      setBreadcrumbs(verifyBreadCrum);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((value.partyCode || party?.partyCode) && partyCode.length) {
      const code = value.partyCode || party?.partyCode;

      const selectedCustomer = partyCode.find((c) => c.value === code);

      setCustomerName(selectedCustomer?.name || "");
    }
  }, [partyCode, value.partyCode, party]);

  useEffect(() => {
    async function getParty() {
      try {
        setLoader({ state: true, message: "Fetching party..." });
        const response = await PartyService.getParty(partyId);
        const response_partycode = await PartyService.getPartyCodes();
        setParty(response);
        setPartyCode([
          {
            label: "None",
            value: null,
            name: null,
          },
          ...(response_partycode?.map(({ customerCode, customerName }) => {
            return {
              label: customerCode,
              value: customerCode,
              name: customerName,
            };
          }) || []),
        ]);
        setPrimaryAgentList(getAgentName(response?.agents, true));
        setAgentList(getAgentName(response?.agents, false));
      } catch (error) {
        const message = getErrorMessage(error);
        enqueueSnackbar(message, {
          variant: "error",
        });
      } finally {
        setLoader({ state: false });
      }
    }
    if (partyId) {
      getParty();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partyId]);

  const handleApprove = async () => {
    try {
      setLoader({ state: true, message: "Verifying party..." });
      const payload = {
        partyCode: value?.partyCode,
        clientName: value?.partyCode ? customerName : null,
        mailOption: value?.mailOption,
        whatsappService: value?.whatsappService ? "yes" : "no",
      };
      const response = await PartyService.approveParty(partyId, payload);
      if (response) {
        enqueueSnackbar(_.startCase(response.message), {
          variant: "success",
        });
        navigate("/dashboard/case-manager/verify-parties");
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      hideAlert();
    }
  };

  const handleApproveStates = async () => {
    try {
      setLoader({ state: true, message: "Verifying Party..." });
      const duplicateDocumentNumCheck = "yes";
      const payload = {
        partyCode: value?.partyCode,
        clientName: value?.partyCode ? customerName : null,
        mailOption: value?.mailOption,
        whatsappService: value?.whatsappService ? "yes" : "no",
      };
      const response = await PartyService.approvePartyStates(
        partyId,
        duplicateDocumentNumCheck,
        payload,
      );
      if (response?.partyCount) {
        showAlert({
          heading: "Verify Party",
          desc: `Another Party exists with same document number, Are you sure you want to verify this Party?`,
          primaryBtnText: "OK",
          secondaryBtnText: "Cancel",
          clickSecondarybtn: () => hideAlert(),
          clickPrimaryBtn: () => {
            handleApprove();
          },
          isOpen: true,
          onDialogClose: () => hideAlert(),
        });
      } else {
        enqueueSnackbar(_.startCase(response.message), {
          variant: "success",
        });
        navigate("/dashboard/case-manager/verify-parties");
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
    }
  };

  const handleUpdateParty = async () => {
    try {
      setLoader({ state: true, message: "Verifying party..." });
      const payload = {
        partyCode: value?.partyCode,
        clientName: value?.partyCode ? customerName : null,
        mailOption: value?.mailOption,
        whatsappService: value?.whatsappService ? "yes" : "no",
      };
      const response = await PartyService.handleUpdateParty(partyId, payload);
      if (response) {
        enqueueSnackbar(_.startCase(response.message), {
          variant: "success",
        });
        navigate("/dashboard/case-manager/parties");
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      hideAlert();
    }
  };

  if (!_.isEmpty(party))
    return (
      <>
        <ActionBar {...{ breadcrumbs, onBreadcrumbClick }} />

        <Container>
          {/* Party Information Section */}
          <Section>
            <SectionTitle>Party Information</SectionTitle>
            <CardGrid>
              {[
                { label: "Party Type", value: _.capitalize(party?.kind) },
                { label: "Party ID", value: party?.id },
                { label: "Party Name", value: party?.name },
                {
                  label: "Document Type",
                  value:
                    party.documentKind === "voter"
                      ? "VOTER ID"
                      : party.documentKind === "aadhar"
                        ? "AADHAAR"
                        : _.upperCase(party.documentKind),
                },
                {
                  label:
                    documentLabels[
                      party.documentKind === "aadhar"
                        ? "aadhaar"
                        : party.documentKind
                    ],
                  value: party?.documentNumber,
                },
              ]?.map((item, index) => (
                <InfoCard key={index}>
                  <InfoLabel>{item.label}</InfoLabel>
                  <InfoValue>{item.value}</InfoValue>
                </InfoCard>
              ))}
            </CardGrid>
          </Section>

          {/* Communication Address Section */}
          <Section>
            <SectionTitle>Communication Address</SectionTitle>
            <CardGrid>
              {[
                { label: "House No", value: party?.houseNumber },
                { label: "Locality", value: party?.locality },
                { label: "Landmark", value: party?.landmark },
                { label: "City", value: party?.city },
                { label: "State", value: party?.state },
                { label: "PIN", value: party?.pin },
              ]?.map((item, index) => (
                <InfoCard key={index}>
                  <InfoLabel>{item.label}</InfoLabel>
                  <InfoValue>{item.value}</InfoValue>
                </InfoCard>
              ))}
            </CardGrid>
          </Section>

          {/* Billing Address Section */}
          {party?.billingHouseNumber ? (
            <Section>
              <SectionTitle>Billing Address</SectionTitle>
              <CardGrid>
                {[
                  { label: "House No", value: party?.billingHouseNumber },
                  { label: "Locality", value: party?.billingLocality },
                  { label: "Landmark", value: party?.billingLandmark },
                  { label: "City", value: party?.billingCity },
                  { label: "State", value: party?.state },
                  { label: "PIN", value: party?.billingPin },
                ]?.map((item, index) => (
                  <InfoCard key={index}>
                    <InfoLabel>{item.label}</InfoLabel>
                    <InfoValue>{item.value}</InfoValue>
                  </InfoCard>
                ))}
              </CardGrid>
            </Section>
          ) : null}

          {/* Documents & Agents Section */}
          <Section>
            <SectionTitle>Documents & Counsels</SectionTitle>
            <DocumentAgentGrid>
              {/* Documents Card */}
              <DocumentCard>
                <CardTitle>Documents</CardTitle>
                <DocumentList>
                  <DocumentLink onClick={() => window.open(party.documentUrl)}>
                    {
                      documentLabels[
                        party.documentKind === "aadhar"
                          ? "aadhaar"
                          : party.documentKind
                      ]
                    }
                  </DocumentLink>
                  {party.gstCertUrl && (
                    <DocumentLink onClick={() => window.open(party.gstCertUrl)}>
                      GST Certificate
                    </DocumentLink>
                  )}
                  {party.authorizationLetterUrl && (
                    <DocumentLink
                      onClick={() => window.open(party.authorizationLetterUrl)}
                    >
                      Authorization Letter
                    </DocumentLink>
                  )}
                </DocumentList>
              </DocumentCard>

              {/* Primary Agents Card */}
              {primaryAgentList?.length > 0 && (
                <AgentCard>
                  <CardTitleWithAction>
                    <CardTitle>Primary Counsel</CardTitle>
                    {party?.agents?.length > 0 && (
                      <ViewDetailsLink
                        onClick={() =>
                          setOpenDrawer((a) => ({ ...a, state: true }))
                        }
                      >
                        View Details
                      </ViewDetailsLink>
                    )}
                  </CardTitleWithAction>
                  <AgentName>{primaryAgentList}</AgentName>
                </AgentCard>
              )}

              {/* Agents Card */}
              {agentList?.length > 0 && (
                <AgentCard>
                  <CardTitle>Agents</CardTitle>
                  <AgentName>{agentList}</AgentName>
                </AgentCard>
              )}
            </DocumentAgentGrid>
          </Section>

          {/* Configuration Section */}
          {/* <Section>
            <SectionTitle>Configuration</SectionTitle>
            <ConfigGrid>
              {inputs.map((arr, index) => (
                <ConfigCard key={index}>
                  <ConfigLabel>{arr.label}</ConfigLabel>
                  {arr.type === "select" && (
                    <CustomSelect
                      white={true}
                      id={arr.name}
                      menuItemValues={arr.menuItems}
                      name={arr.name}
                      value={value[arr.name] || party?.partyCode}
                      onChange={(e) => {
                        const selectedCode = e?.target?.value;

                        const selectedCustomer = partyCode.find(
                          (c) => c.value === selectedCode,
                        );

                        setValue((prev) => ({
                          ...prev,
                          [arr.name]: selectedCode,
                        }));

                        setCustomerName(selectedCustomer?.name || "");
                      }}
                    />
                  )}

                  {arr.type === "text" && arr.name === "customerName" && (
                    <InfoValue style={{ marginTop: "8px" }}>
                      {customerName || "-"}
                    </InfoValue>
                  )}

                  {arr.type === "radio" && (
                    <RadioGroup style={{ flexDirection: "row", gap: "20px" }}>
                      {arr.menuItems.map((radio, key) => (
                        <FormControlLabel
                          key={key}
                          classes={{ label: classes.formLabel }}
                          checked={value?.mailOption === radio.value}
                          onChange={(event) =>
                            setValue((prev) => ({
                              ...prev,
                              mailOption: radio.value,
                            }))
                          }
                          control={
                            <Radio size="medium" style={{ color: "#00838c" }} />
                          }
                          label={radio.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                  {arr.type === "radioService" && (
                    <RadioGroup style={{ flexDirection: "row", gap: "20px" }}>
                      {arr.menuItems.map((radio, key) => (
                        <FormControlLabel
                          key={key}
                          classes={{ label: classes.formLabel }}
                          control={
                            <Radio
                              size="medium"
                              style={{ color: "#00838c" }}
                              checked={value?.whatsappService !== radio.value}
                              onClick={(event) =>
                                setValue((prev) => ({
                                  ...prev,
                                  whatsappService:
                                    prev.whatsappService === radio.value
                                      ? true
                                      : false,
                                }))
                              }
                            />
                          }
                          label={radio.label}
                        />
                      ))}
                    </RadioGroup>
                  )}
                </ConfigCard>
              ))}
            </ConfigGrid>
          </Section> */}

          {/* Action Buttons */}
          <ActionSection>
            {party?.status === "underReview" && (
              <ActionButtonWrapper>
                <PrimaryCTAButton onClick={handleApproveStates}>
                  {labels.verify_party}
                </PrimaryCTAButton>
              </ActionButtonWrapper>
            )}
            {party?.status === "active" && (
              <ActionButtonWrapper>
                <PrimaryCTAButton onClick={handleUpdateParty}>
                  Update Party
                </PrimaryCTAButton>
              </ActionButtonWrapper>
            )}
          </ActionSection>
        </Container>

        <AgentListModal
          {...{
            opendrawer,
            setOpenDrawer,
            MTRef,
            partyList: [{ party }],
            displayAuthUrl: true,
          }}
        />
      </>
    );
  else return null;
};

export default Index;

// Styled Components
export const Container = styled.div`
  padding: 32px 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e1e3ee 100%);
  min-height: 100vh;
  @media (max-width: 768px) {
    padding: 16px 12px;
    gap: 20px;
  }
`;

export const Section = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.3s ease;
  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  }
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

export const SectionTitle = styled.h2`
  font-family: ${theme.fonts.primaryFontBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid ${COLORS.BTN_GREEN};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 12px;
  }
`;

export const InfoCard = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: default;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.1);
  }
`;

export const InfoLabel = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #7a8fa3;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 0;
  font-weight: 500;
`;

export const InfoValue = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 15px;
  margin: 0;
  word-break: break-word;
`;

export const DocumentAgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const DocumentCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #e8eef5;
`;

export const AgentCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #e8eef5;
`;

export const CardTitle = styled.h3`
  font-family: ${theme.fonts.primaryFontBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 14px;
  margin: 0 0 12px 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

export const CardTitleWithAction = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

export const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const DocumentLink = styled.a`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: ${COLORS.BTN_GREEN};
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 8px 0;
  border-bottom: 2px solid transparent;
  display: inline-block;
  &:hover {
    text-decoration: underline;
    // color: darken(${COLORS.BTN_GREEN}, 10%);
    // border-bottom-color: ${COLORS.BTN_GREEN};
  }
`;

export const ViewDetailsLink = styled.button`
  background: none;
  border: none;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: ${COLORS.BTN_GREEN};
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  transition: color 0.3s ease;
  &:hover {
    color: darken(${COLORS.BTN_GREEN}, 10%);
  }
`;

export const AgentName = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 13px;
  margin: 0;
  line-height: 1.6;
`;

export const ConfigGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const ConfigCard = styled.div`
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 8px;
  border: 1px solid #e8eef5;
`;

export const ConfigLabel = styled.label`
  font-family: ${theme.fonts.primaryFontBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 12px;
  display: block;
  margin-bottom: 12px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.3px;
`;

export const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
`;

export const ActionButtonWrapper = styled.div`
  min-width: 180px;
  display: flex;
`;

export const HyperLink = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${COLORS.BTN_GREEN};
  text-decoration: underline;
  cursor: pointer;
`;
